import { ScriptTipResult } from "./ScriptTipResult";

export function getPythonScriptTips(
  errorValue: string
): ScriptTipResult | null {
  if (!errorValue) {
    return null;
  }

  if (/No such file/.test(errorValue)) {
    return {
      htmlToShow:
        "Having trouble finding a file? You'll find some useful tips in our <a href='https://www.kaggle.com/dansbecker/finding-your-files-in-kaggle-kernels' target='_blank'>Finding Your Files tutorial</a>.",
      scriptTipCode: "py-files-tute",
    };
  }

  if (/name '(FALSE|false|TRUE|true)' is not defined/.test(errorValue)) {
    return {
      textToShow:
        "In Python, True and False have initial capitals and the rest lower case.",
      scriptTipCode: "py-truefalse",
    };
  }

  if (/module 'pandas' has no attribute '[Dd]ata[Ff]rame'/.test(errorValue)) {
    return {
      textToShow: "Did you mean pandas.DataFrame?",
      scriptTipCode: "py-pddf",
    };
  }

  if (/name 'xrange' is not defined/.test(errorValue)) {
    return {
      textToShow: "In Python 3, 'xrange' is just 'range'.",
      scriptTipCode: "py-range",
    };
  }

  if (/name 'plot' is not defined/.test(errorValue)) {
    return {
      textToShow: "You might be missing 'from matplotlib.pyplot import plot'.",
      scriptTipCode: "py-plot",
    };
  }

  if (
    /invalid literal for int\(\) with base 10: 'nan'/.test(errorValue) ||
    /Input contains NaN, infinity or a value too large/.test(errorValue)
  ) {
    return {
      htmlToShow:
        "You might need a way of handling missing values, such as pandas.DataFrame.fillna or sklearn.preprocessing.Imputer. See our <a href='https://www.kaggle.com/dansbecker/handling-missing-values' target='_blank'>Missing Values tutorial</a> for more details.",
      scriptTipCode: "py-nan-tute",
    };
  }

  if (/could not convert string to float/.test(errorValue)) {
    return {
      htmlToShow:
        "You have categorical data, but your model needs something numerical. See our <a href='https://www.kaggle.com/dansbecker/using-categorical-data-with-one-hot-encoding' target='_blank'>one hot encoding tutorial</a> for a solution.",
      scriptTipCode: "py-onehot-tute",
    };
  }

  const xgbRegex = /^No module named '(x[gbos]+t)'\s*$/;
  if (xgbRegex.test(errorValue)) {
    const xgbModuleName = xgbRegex.exec(errorValue)![1];
    return {
      textToShow: `We don't have the "${xgbModuleName}" module. Did you mean "xgboost"?`,
      scriptTipCode: "py-xgb",
    };
  }

  if (/^No module named 'BeautifulSoup'\s*$/.test(errorValue)) {
    return {
      textToShow: "You might be missing 'from bs4 import BeautifulSoup'",
      scriptTipCode: "py-bs4",
    };
  }

  if (/^No module named 'pytorch'$/.test(errorValue)) {
    return {
      textToShow:
        "If you want to load PyTorch, use 'import torch', not 'import pytorch'.",
      scriptTipCode: "py-pytorch",
    };
  }

  // Note that this generic handler for a ModuleNotFoundError comes _after_ the specific cases like pytorch handled above.
  // TODO(mbooth): This should read errorName
  if (errorValue === "ModuleNotFoundError") {
    return {
      htmlToShow:
        "Are we missing a package you need? You can add it on the Settings tab, and/or send us a <a href='https://github.com/Kaggle/docker-python' target='_blank'>pull request</a>.",
      scriptTipCode: "py-module-notfound",
    };
  }

  if (/name 'pd' is not defined/.test(errorValue)) {
    return {
      textToShow: "You might be missing 'import pandas as pd'",
      scriptTipCode: "py-import-pandas",
    };
  }

  if (/Missing parentheses in call to 'print'/.test(errorValue)) {
    return {
      textToShow:
        'Python 3 requires parentheses around print arguments, e.g. - print("kaggle")',
      scriptTipCode: "py-print-parentheses",
    };
  }

  if (/bq_helper/.test(errorValue)) {
    return {
      htmlToShow:
        "bq_helper is planned to be deprecated. Please consider using " +
        "<code>from google.cloud import bigquery</code> for reproducible notebooks." +
        " Please see <a href='https://cloud.google.com/bigquery/docs/reference/libraries'" +
        " target='_blank'>BigQuery Documentation</a> for details",
      scriptTipCode: "bq-helper-deprecated",
    };
  }

  if (
    /The user .* does not have bigquery\..* permission in project/.test(
      errorValue
    )
  ) {
    return {
      textToShow:
        "You do not have access to this BigQuery project. Please choose another project or link an account with appropriate permissions.",
      scriptTipCode: "bq-project-permission",
    };
  }

  if (/Google Cloud SDK credential not found/.test(errorValue)) {
    return {
      textToShow:
        "Google Cloud SDK credential not found. Please ensure you have a authorized Google Cloud SDK for this Notebook (in Menu, Add-ons > Google Cloud SDK).",
      scriptTipCode: "gcloud-no-credential",
    };
  }

  if (/No user secrets exist for kernel id .* and label/.test(errorValue)) {
    return {
      textToShow:
        "Please ensure you have added the user secret you are trying to access to your Notebook (in Menu, Add-ons > Secrets).",
      scriptTipCode: "usersecret-no-secret",
    };
  }

  if (/No user secrets exist for kernel id .*/.test(errorValue)) {
    return {
      textToShow:
        "Please ensure you have linked a Google account in the Notebook Add-ons menu.",
      scriptTipCode: "gcp-no-credential",
    };
  }

  if (
    /GET_USER_SECRET_BY_LABEL(.|[\r\n])*Connection error trying to communicate with service./.test(
      errorValue
    )
  ) {
    return {
      textToShow:
        "Please ensure you have internet turned on for the Notebook when trying to access User Secrets.",
      scriptTipCode: "usersecret-no-internet",
    };
  }

  if (/use the latest docker environment./.test(errorValue)) {
    return {
      textToShow: "Please, change Docker Environment to the latest.",
      scriptTipCode: "gcp-docker-env",
    };
  }

  if (
    /Unable to refresh access token due to connection error./.test(errorValue)
  ) {
    return {
      textToShow:
        "Please ensure you have internet turned on for the Notebook when trying to use Google Cloud Services.",
      scriptTipCode: "gcp-no-internet",
    };
  }

  if (/Unable to refresh access token./.test(errorValue)) {
    return {
      textToShow:
        "There is a problem with the linked account credentials. You may be able " +
        "to fix this by going to your Kaggle account page, revoking access to the " +
        "linked account, and then linking the account again in the Notebook editor " +
        "Add-Ons Menu, Google Cloud Services.",
      scriptTipCode: "gcp-invalid-credential",
    };
  }

  if (
    errorValue.includes("get_gcs_path") &&
    errorValue.includes("Timeout error trying")
  ) {
    return {
      textToShow:
        "Making a copy of a large dataset may take several minutes. If internet " +
        "is turned on, please wait several minutes and try again.",
      scriptTipCode: "gcs-copy-timeout",
    };
  }

  if (/Response too large to return./.test(errorValue)) {
    return {
      textToShow:
        "Please consult the configuration of your BigQuery project to allow larger results.",
      scriptTipCode: "bq-results-too-large",
    };
  }

  // TODO(aagundez): Figure out how to make these BigQuery script tips less fragile when opening network access
  // for paid compute
  if (/400 Function not found/.test(errorValue)) {
    return {
      htmlToShow:
        'Here’s a <a href="https://cloud.google.com/bigquery/docs/reference/standard-sql/functions-and-operators" target="_blank">handy resource</a> for using BigQuery functions and operators.',
      scriptTipCode: "bq-invalid",
    };
  }

  if (/429 GET/.test(errorValue)) {
    return {
      textToShow:
        "You have exceeded your 30-day BigQuery quota. The quota refreshes on a rolling basis.",
      scriptTipCode: "bq-quota-exceeded",
    };
  }

  if (/502(.*)bigquery/.test(errorValue)) {
    return {
      htmlToShow:
        'Your request could not be completed. If you continue to see this error, let us know using the <a href="https://www.kaggle.com/product-feedback" target="_blank">Product Feedback forum.</a>',
      scriptTipCode: "bq-bad-gateway",
    };
  }

  if (/Anonymous credentials cannot be refreshed/.test(errorValue)) {
    return {
      htmlToShow:
        'Your request could not be completed. If you continue to see this error, let us know using the <a href="https://www.kaggle.com/product-feedback" target="_blank">Product Feedback forum.</a>',
      scriptTipCode: "bq-value-error",
    };
  }

  if (/400(.*)bigquery/.test(errorValue)) {
    return {
      htmlToShow:
        'Need help with a query? Check out the <a href="https://cloud.google.com/bigquery/docs/reference/standard-sql/query-syntax" target="_blank">BigQuery syntax documentation.</a>',
      scriptTipCode: "bq-syntax-error",
    };
  }

  if (/404(.*)bigquery/.test(errorValue)) {
    return {
      htmlToShow:
        'The requested resource doesn’t exist. See all available <a href="https://www.kaggle.com/datasets?filetype=bigQuery" target="_blank">BigQuery datasets here.</a>',
      scriptTipCode: "bq-not-found",
    };
  }

  const tpuRegex =
    /OpKernel for 'TPU' devices compatible.*\n.*Requested Attributes: T=(\S+)/;
  if (tpuRegex.test(errorValue)) {
    const tpuDataType = tpuRegex.exec(errorValue)![1];
    return {
      htmlToShow: `TPUs do not support ${tpuDataType} data type for your operation. Please check that the operation data types are supported by <a href="https://cloud.google.com/tpu/docs/troubleshooting#unsupported_data_type" target="_blank">TPUs</a>.`,
      scriptTipCode: "tpu-data-type-not-found",
    };
  }

  if (/'DataFrame' object has no attribute 'read_csv'/.test(errorValue)) {
    return {
      textToShow:
        "Do you mean pd.read_csv(...)? (In Pandas, read_csv is a module-level function, not a method on DataFrame.)",
      scriptTipCode: "py-pandas-read_csv",
    };
  }

  if (/The truth value of a Series is ambiguous/.test(errorValue)) {
    return {
      textToShow:
        "To combine filtering conditions in Pandas, use bitwise operators ('&' and '|') not pure Python ones ('and' and 'or')",
      scriptTipCode: "py-series-bool",
    };
  }

  if (/unhashable type: 'slice'/.test(errorValue)) {
    return {
      htmlToShow:
        "NumPy-style indexing doesn't work on a Pandas DataFrame; use <pre>loc</pre> or <pre>iloc</pre>. For a refresher, see our <a href='https://www.kaggle.com/residentmario/indexing-selecting-assigning-reference' target='_blank'>reference guide</a>.",
      scriptTipCode: "py-pandas-iloc",
    };
  }

  if (/name '(q|step_)[0-9]' is not defined/.test(errorValue)) {
    // Exercise helpers in the Learn Python track have names like q1, q2, etc. The ML track uses names like step_1, step_2...
    return {
      htmlToShow:
        "Before working on a course exercise, make sure you run the setup code at the top the notebook. If returning to the notebook after taking a break, always run all of the code from top to bottom before continuing your work.",
      scriptTipCode: "py-learn-exercise-setup",
    };
  }

  return null;
}
